<template>
  <div class="tab-container-task-create">
    <div class="task-create-back">
      <a href="javascript:void(0)" @click="onBack"><span class="icon-caret-left"></span> Back</a>
    </div>
    <hr />
    <div class="task-create-body">
      <div class="task-create-body-information">
        <div class="information-left">
          <img class="img-fluid" :src="task.task_thumbnail" :alt="task.task_thumbnail" />
        </div>
        <div class="information-right">
          <h3>{{ task.task_name }}</h3>
          <h3>Topic</h3>
          <div class="topic">{{ task.topic }}</div>
        </div>
      </div>
      <hr />
      <div class="task-create-body-card">
        <h4>Task type</h4>
        {{taskType}}
      </div>

      <div v-if="task.summary" class="task-create-body-card task-create-body-card--container">
        <h4>SUMMARY</h4>
        <p class="description" v-html="task.summary"></p>
      </div>

      <div v-if="task.language" class="task-create-body-card task-create-body-card--container">
        <h4>LANGUAGE</h4>
        <p class="description">{{ language }}</p>
      </div>

      <div v-if="task.level" class="task-create-body-card task-create-body-card--container">
        <h4>LEVEL</h4>
        <p class="description">{{ level }}</p>
      </div>

      <div v-if="task.country" class="task-create-body-card task-create-body-card--container">
        <h4>COUNTRY</h4>
        <p class="description">{{ country }}</p>
      </div>

      <div
        v-if="task.teacher_instructions"
        class="task-create-body-card task-create-body-card--container"
      >
        <h4>TEACHER INSTRUCTIONS</h4>
        <p class="description" v-html="task.teacher_instructions"></p>
      </div>

      <div
        class="task-create-body-card task-create-body-card--container"
        v-if="task.can_do_statement"
      >
        <h4>LEARNING OBJECTIVES</h4>
        <p class="description" v-html="task.can_do_statement"></p>
      </div>

      <div
        class="task-create-body-card task-create-body-card--container"
        v-if="task.create_prompt || task.video_url"
      >
        <h4>STUDENT PROMPT</h4>
        <video-custom v-if="task.video_url" :src="task.video_url" :full-screen-button="false" />
        <p class="description" v-html="task.create_prompt"></p>
      </div>

      <div v-if="task.due_date" class="task-create-body-card">
        <h4>STUDENT PROMPT DUE DATE</h4>
        <span class="text text--date">{{ task.due_date | dateMonthDayYear }}</span>
      </div>
      <div
        class="task-create-body-card task-create-body-card--container"
        v-if="!isIndependentNoResponse && task.respond_prompt"
      >
        <h4>RESPONSE PROMPT</h4>
        <p class="description" v-html="task.respond_prompt"></p>
      </div>

      <div v-if="!isIndependentNoResponse && task.response_due_date" class="task-create-body-card">
        <h4>RESPOND PROMPT DUE DATE</h4>
        <span class="text text--date">{{ task.response_due_date | dateMonthDayYear }}</span>
      </div>

      <div v-if="task.materials.length" class="task-create-body-card">
        <h4>STUDENT SUPPORT MATERIALS</h4>

        <b-list-group horizontal>
          <b-list-group-item
            class="attachment-item"
            v-for="(item, index) in task.materials"
            :key="'task-materials-' + index"
            :href="item.url"
            target="_blank"
          >
            <thumbnail-attachment :data="item" />
            <b-tooltip :target="'task-materials-' + index" triggers="hover" placement="bottom">
              {{ item.label }}
            </b-tooltip>
            <div class="attachment-item__label" :id="'task-materials-' + index">
              {{ truncateString(item.label, 20) }}
            </div>
          </b-list-group-item>
        </b-list-group>
      </div>

      <div v-if="task.links.length" class="task-create-body-card">
        <h4>ADDITIONAL LINKS</h4>
        <ul>
          <li v-for="(item, index) in task.links" :key="'task-links-' + index" class="link-label">
            <b-link :href="item.url">{{ item.label }}</b-link>
          </li>
        </ul>
      </div>

      <div v-if="task.media.length" class="task-create-body-card">
        <h4>MEDIA</h4>
        <b-list-group horizontal>
          <b-list-group-item
            class="attachment-media"
            v-for="(item, index) in task.media"
            :key="'task-media-' + index"
          >
            <span v-if="item.type === 'transcoder'" class="attachment-media-item">
              <thumbnail-attachment :data="item" />
              <b-tooltip :target="'task-media-' + index" triggers="hover">
                {{ item.label }}
              </b-tooltip>
              <div class="attachment-media-item__label" :id="'task-media-' + index">
                {{ truncateString(item.label, 20) }}
              </div>
            </span>
            <a v-else :href="item.url" target="_blank" class="attachment-media-item">
              <thumbnail-attachment :data="item" />
              <b-tooltip :target="'task-media-' + index" triggers="hover">
                {{ item.label }}
              </b-tooltip>
              <div class="attachment-media-item__label" :id="'task-media-' + index">
                {{ truncateString(item.label, 20) }}
              </div>
            </a>
          </b-list-group-item>
        </b-list-group>
      </div>

      <hr />
      <div v-if="!isIndependentNoResponse" class="task-create-body-card">
        <h4>Task Responses</h4>
        {{ responseType }}
      </div>

      <div class="task-create-body-card" v-if="!isIndependentNoResponse">
        <h4>TARGET NUMBER OF RESPONSES</h4>
        <span class="text">{{ task.number_responses }}</span>
      </div>

      <div class="task-create-body-card">
        <h4>TIME LIMIT ON VIDEO POST</h4>
        <span class="text">{{ task.video_time_limit }} seconds</span>
      </div>

      <div class="task-create-body-card task-create-body-card--buttons">
        <b-button @click="onBack" btn-text="Cancel" style="margin-right: 10px" />
        <b-button @click="showDuedateModal" btn-text="Add to Classroom" />
      </div>
    </div>
    <modal
      id="setDuedateModal"
      @hidden="hideDuedateModal"
      size="md"
      :hide-footer="true"
      :hide-close-button="true"
      modal-class="set-duedate-modal"
    >
      <template #body>
        <p>Please select a due date:</p>
        <date-picker v-model="duedateClassroom" placeholder="Due Date" :min="minStartDate" />

        <div class="container-buttons">
          <b-button @click="addToClassroom" btn-text="Skip" style="margin-right: 10px" />
          <b-button @click="addToClassroom" btn-text="Ok" />
        </div>
        <b-overlay no-wrap :show="isLoading" />
      </template>
    </modal>
  </div>
</template>

<script>
import { Button } from '@/core/components/Button'
import { ClassroomMixins } from '@/core/Mixins'
import ThumbnailAttachments from '@/core/components/Thumbnail/ThumbnailAttachments'
import { userTypeMixins } from '@/core/Mixins'
import { taskCategories } from '@/core/Utils'
import { VideoCustom } from '@/core/components/Video'
import { Modal } from '@/core/components/Modal'
import DatePicker from '@/core/components/DatePicker/DatePicker'
import { ClassroomService } from '@/core/Services'

export default {
  name: 'ClassroomDetailTabMyTaskLibraryDetail',
  mixins: [ClassroomMixins, userTypeMixins],
  components: {
    'b-button': Button,
    'thumbnail-attachment': ThumbnailAttachments,
    VideoCustom,
    Modal,
    DatePicker
  },
  data() {
    return {
      response_types: [
        { id: 1, name: 'Video Response' },
        { id: 2, name: 'Text Response' }
      ],
      taskCategoriesList: [
        { id: taskCategories.independentPeerResponse, name: 'Allow Peer Responses' },
        { id: taskCategories.independentNoResponse, name: 'No Peer Responses' }
      ],
      isLoading: false,
      minStartDate: new Date(),
      duedateClassroom: null
    }
  },
  props: {
    task: [Array, Object]
  },
  methods: {
    onBack() {
      this.$emit('on-change-component', 'ClassroomDetailTabMyTasksLibraryList')
    },
    addToClassroom() {
      ClassroomService.addIndependentTask(
        this.classroom.id,
        this.task.id,
        this.duedateClassroom
      ).then((response) => {
        if (response.success) {
          this.$bvModal
            .msgBoxOk('Successful!', {
              size: 'sm',
              dialogClass: 'modal-message-box',
              noCloseOnBackdrop: true,
              centered: true
            })
            .then(() => {
              this.$emit('on-change-component', 'ClassroomDetailTabMyTasksList')
            })
        } else {
          const errorMessage = response.message ? response.message : 'Error adding task.'
          this.$bvModal.msgBoxOk(`${errorMessage} Please try again.`, {
            size: 'sm',
            dialogClass: 'modal-message-box',
            noCloseOnBackdrop: true,
            centered: true
          })
        }
      })
    },
    truncateString(str, num) {
      return str?.length > num ? str.slice(0, num) + '...' : str
    },
    hideDuedateModal() {
      this.$bvModal.hide('setDuedateModal')
    },
    showDuedateModal() {
      this.$bvModal.show('setDuedateModal')
    }
  },
  computed: {
    taskType() {
      let selectedCategory = this.taskCategoriesList.find((task_category)=>task_category.id === this.task.task_category)
      return selectedCategory !== undefined ? selectedCategory.name : 'You have not chosen a category type.'
    },
    responseType() {
      let selectedType = this.response_types.find((response_type)=>response_type.id === this.task.response_type)
      return selectedType !== undefined ? selectedType.name : 'You have not chosen a response type.'
    },
    isIndependentNoResponse() {
      return this.task.task_category === taskCategories.independentNoResponse
    },
    language() {
      return this.task?.task_language?.name
    },
    level() {
      return this.task?.level?.name
    },
    country() {
      return this.task?.country?.name
    }
  }
}
</script>

<style lang="stylus" scoped>
@import "~@/core/styles/variables.styl"
@import "~@/core/styles/mixins.styl"

.tab-container-task-create
  padding-left 10px
  padding-right 10px
  padding-bottom 80px
  padding-top 13px

hr
  margin 1rem 0

.task-create-body
  padding 0

  h3
    color color-8
    font font-opensans-bold
    font-size 24px
    margin-bottom 24px
    text-overflow ellipsis
    word-break break-word

  &-information
    padding 0 0 13px 0
    display flex

    .information-left
      width 35%

      ::v-deep
        .img-fluid
          min-width: 80px
          min-height: 80px
          object-fit cover

    .information-right
      padding-left 18px
      font font-opensans-regular
      font-size 14px
      color color-8

      div
        margin-bottom 11px

      .topic
      .tags
        font-size 16px

  &-card
    margin-bottom 30px

    &--description
      margin-top 30px

    &--container
      margin-top 54px
      text-overflow ellipsis
      word-break break-all

      .video-overlay
        width 100%
        height 395px
        border-radius 4px
        margin-bottom 10px

    &--buttons
      display flex
      justify-content center

    h4
      font font-opensans-semibold
      font-size 18px
      text-transform uppercase
      margin-bottom 9px
      color color-8

    .description
      font font-opensans-regular
      font-size 14px
      color color-21

    .text
      font font-opensans-regular
      color color-17
      font-size 14px

      &--date
        font font-opensans-semibold
        font-size 14px
        color color-8

    ul
      padding 0
      margin 0
      list-style-type none
      display block

      & > li
        border 0
        padding 0
        display block
        color color-3

        a
          font font-opensans-semibold
          font-size 14px
          text-decoration underline
          color color-34

  @media (min-width: 992px)
    padding-left 150px
    padding-right 150px

    &-information
      .information-left
      width 110px

      ::v-deep
        .img-fluid
          max-width 110px
          height: 110px
          min-width: 110px

    .information-right
      padding-left 30px

  @media (min-width: 1400px)
    padding-left 251px
    padding-right 251px


.task-create-back
  a
    font font-opensans-bold
    font-size 17px
    cursor pointer
    text-decoration none
    color color-3
    display flex
    align-items center

    span
      font-size 0.7em
      padding-right 5px

      &:before
        color color-3

.list-group
  overflow-x auto

.list-group-item-action.attachment-item
  width unset
  display flex
  flex-direction column
  align-items center
  margin-right 10px
  border 2px solid color-3 !important

  .attachment-item__label
    margin-top 6px
    font-weight 800
    text-transform uppercase
    font-size 14px

.attachment-media
  border 2px solid color-3 !important
  padding 0
  margin-right 10px
  cursor pointer

  .attachment-media-item
    width unset
    display flex
    flex-direction column
    align-items center
    position relative
    padding 0.75rem 1.25rem

    .attachment-media-item__label
      margin-top 6px
      font font-opensans-semibold
      font-size 14px
      color black
      text-transform uppercase

.link-label
  text-transform uppercase

.container-visible
  display flex

  .icon-mark
    width 38px

  .text--label-visible
    margin-left 15px
    display flex
    align-items center

.container-buttons
  display flex
  justify-content center
</style>
<style lang="stylus">
.set-duedate-modal
  .modal-dialog
    .modal-content
      overflow visible
      .modal-body
        overflow visible

        p
          margin-top 0
          margin-bottom 1rem
</style>
